import React, { FC, useCallback, useMemo, useRef } from "react";
import { ScrollView as RNScrollView } from "react-native";
import { useNavigate } from "react-router";
import { ProductPreview } from "../../../../domain/projections/ProductPreview";
import { useGetExperiments } from "../../behaviors/useGetExperiments";
import { useGetTrendingForYouLooks } from "../../behaviors/useGetTrendingForYouLooks";
import { useHardwareBackPress } from "../../behaviors/useHardwareBackPress";
import { TrendingForYou as TrendingForYouTemplate } from "../../components/templates/trendingForYou/TrendringForYou";
import { InViewProvider } from "../../hooks/useInView";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { Routes } from "../../routing/routes";
import { STORAGE_KEY_TRENDING_FOR_YOU_SCROLL } from "../../shared/util/storageKeys";
import { List, TrackingPage, TrackingView } from "../../tracking/tracking";
import { useTrackCategoriesSelected } from "../../tracking/useTrackCategoriesSelected";
import { useTrackItemSelected } from "../../tracking/useTrackItemSelected";
import { useTrackPageView } from "../../tracking/useTrackPageView";
import { useTrackShopButtonClicked } from "../../tracking/useTrackShopButtonClicked";
import { useTrackViewItemList } from "../../tracking/useTrackViewItemList";

const DEFAULT_SCROLL_VALUE = 0;

interface TrendingForYouProps {
  readonly onBack: () => void;
  readonly onBackToStyleProfile: () => void;
}

const TrendingForYou: FC<TrendingForYouProps> = ({ onBack, onBackToStyleProfile }) => {
  const scrollViewRef = useRef<RNScrollView>(null);
  const navigate = useNavigate();
  const {
    session: { user },
    store,
    basePath,
    tradename,
  } = useStaticInfo();

  const trackingCommonParams = useMemo(
    () => ({
      country: user.country,
      segment: user.segment,
      userId: user.id,
      tradename,
    }),
    [tradename, user.country, user.id, user.segment],
  );
  useTrackPageView({
    ...trackingCommonParams,
    page: TrackingPage.TRENDING_FOR_YOU,
    view: TrackingView.LOOK_IN_TRENDING_FOR_YOU,
  });
  const trackCategoriesSelected = useTrackCategoriesSelected(trackingCommonParams);
  const trackShopButtonClicked = useTrackShopButtonClicked(trackingCommonParams);
  const trackItemSelected = useTrackItemSelected(trackingCommonParams);
  const trackViewItemList = useTrackViewItemList(trackingCommonParams);

  const { looks, isLoading } = useGetTrendingForYouLooks();
  const { experiments } = useGetExperiments();

  const scrollValueRef = useRef<number>(DEFAULT_SCROLL_VALUE);

  const saveScrollValueInStore = useCallback(
    async (value = 0) => {
      await store.set(STORAGE_KEY_TRENDING_FOR_YOU_SCROLL, value < 0 ? 0 : value);
    },
    [store],
  );

  const handleOnCrossNavigation = useCallback(
    (buttonId: string) => {
      trackShopButtonClicked({
        buttonId,
      });

      trackCategoriesSelected();

      saveScrollValueInStore(DEFAULT_SCROLL_VALUE);

      navigate(`${basePath}/${Routes.CATEGORIES}`);
    },
    [basePath, navigate, saveScrollValueInStore, trackCategoriesSelected, trackShopButtonClicked],
  );

  const handleOnPressLook = useCallback(
    ({ lookId, product }: { lookId: string; product: ProductPreview }) => {
      saveScrollValueInStore(scrollValueRef.current);

      trackItemSelected({
        list: List.TRENDING_FOR_YOU,
        productPreview: product,
        lookId,
      });
    },
    [saveScrollValueInStore, trackItemSelected],
  );

  const handleOnScroll = useCallback((coords: { X: number; Y: number }) => {
    scrollValueRef.current = coords.Y;
  }, []);

  const handleOnLayoutLooks = useCallback(async () => {
    if (looks && !isLoading) {
      trackViewItemList({
        experiments,
        list: List.TRENDING_FOR_YOU,
        looks,
      });
    }

    const storedScroll = await store.get(STORAGE_KEY_TRENDING_FOR_YOU_SCROLL);

    if (Number(storedScroll) >= 0 && scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ x: 0, y: Number(storedScroll), animated: false });
    }
  }, [experiments, isLoading, looks, store, trackViewItemList]);

  useHardwareBackPress(onBack);

  return (
    <>
      <InViewProvider key="trending-for-you">
        <TrendingForYouTemplate
          isLoading={isLoading}
          looks={looks}
          scrollViewRef={scrollViewRef}
          onBack={onBack}
          onBackToStyleProfile={onBackToStyleProfile}
          onCrossNavigation={handleOnCrossNavigation}
          onLayoutLooks={handleOnLayoutLooks}
          onPressLook={handleOnPressLook}
          onScroll={handleOnScroll}
        />
      </InViewProvider>
    </>
  );
};

export { TrendingForYou };
